import { z } from 'zod'
import type { schema as slideDeckSchema } from '../firestore/SlideDeck/schema'
import type { schema as slideSchema } from '../firestore/Slide/schema'
import type { schema as slideQuestionSchema } from '../firestore/SlideQuestion/schema'
import type { schema as slideDeckExhibitSchema } from '../firestore/SlideDeckExhibit/schema'
import type { schema as slideDeckRoleSchema } from '../firestore/SlideDeckRole/schema'
import type { schema as slideDeckMaterialSchema } from '../firestore/SlideDeckMaterial/schema'
import type { schema as slideRubricSchema } from '../firestore/SlideRubric/schema'

export type DocWithId<T> = { id: string; data: T }

export const slideDeckIncludeEnum = z.enum([
  'slides',
  'questions',
  'exhibits',
  'roles',
  'materials',
  'rubrics',
])

export type SlideDeckInclude = z.infer<typeof slideDeckIncludeEnum>

// The v2 (Adonis) read also returns the deck's authors and references, which
// the instructor Course Materials tab shows. They stay out of
// slideDeckIncludeEnum because that enum is the v1 callable's input contract
// and the callable has no reader for them.
export type SlideDeckApiInclude = SlideDeckInclude | 'authors' | 'references'

export type IncludeDataMap = {
  slides: DocWithId<z.infer<typeof slideSchema>>[]
  questions: DocWithId<z.infer<typeof slideQuestionSchema>>[]
  exhibits: DocWithId<z.infer<typeof slideDeckExhibitSchema>>[]
  roles: DocWithId<z.infer<typeof slideDeckRoleSchema>>[]
  materials: DocWithId<z.infer<typeof slideDeckMaterialSchema>>[]
  rubrics: DocWithId<z.infer<typeof slideRubricSchema>>[]
}

// Shared result type — used by both the callable function and client cubits
type ApiDoc = { id: string; data: Record<string, unknown> }

/**
 * Why the server refused a participant the slide deck on ACCESS_DENIED, so the
 * client can say what the user needs instead of a generic "no access".
 *  - section_pass_required: a Section Pass section and the user has no pass
 *  - purchase_required:     a token section and the user hasn't bought it
 *  - codeless_opted_out:    a codeless-access section the user opted out of
 */
export type SlideDeckAccessDenialReason =
  | 'section_pass_required'
  | 'purchase_required'
  | 'codeless_opted_out'

export type GetSlideDeckForAssignmentResult =
  | {
      success: true
      slideDeck: ApiDoc
      slides?: ApiDoc[]
      questions?: ApiDoc[]
      exhibits?: ApiDoc[]
      roles?: ApiDoc[]
      materials?: ApiDoc[]
      rubrics?: ApiDoc[]
      // v2 only, see SlideDeckApiInclude.
      authors?: ApiDoc[]
      references?: ApiDoc[]
    }
  | {
      success: false
      error: string
      errorCode: 'NOT_FOUND' | 'NOT_MEMBER' | 'ACCESS_DENIED' | 'INTERNAL_ERROR'
      // Set only on ACCESS_DENIED. Optional so older servers still parse.
      denialReason?: SlideDeckAccessDenialReason
      // On ACCESS_DENIED and NOT_MEMBER the deck metadata (name, price, free)
      // still comes back so the UI shell (paywall / meeting deck name) can
      // render — the content subcollections do not. Absent on NOT_FOUND and
      // INTERNAL_ERROR.
      slideDeck?: ApiDoc
    }

// Max deck ids per GET /api/v1/slide_decks/summaries request. Keeps the query
// string around 3KB; callers with more ids split them across requests.
export const SLIDE_DECK_SUMMARIES_MAX_IDS = 100

// Response of GET /api/v1/slide_decks/summaries: the deck-document fields the
// Student Assignment List renders, in Firestore field names. Ids the caller
// can't read, or that don't exist, are left out.
export type GetSlideDeckSummariesResult = {
  slideDecks: ApiDoc[]
}

export type SlideDeckAccessResult<T extends SlideDeckInclude[]> = {
  success: true
  slideDeck: DocWithId<z.infer<typeof slideDeckSchema>>
} & Pick<IncludeDataMap, T[number]>

export type SlideDeckAccessErrorCode =
  | 'NOT_FOUND'
  | 'NOT_MEMBER'
  | 'ACCESS_DENIED'
  | 'INTERNAL_ERROR'

export type SlideDeckAccessError = {
  success: false
  error: string
  errorCode: SlideDeckAccessErrorCode
}
